import { allocateCents } from '../money.js';
import { nowISO, parts } from '../dates.js';
import { newId } from '../ids.js';
import { KeelError } from '../errors.js';
/**
 * `allocateCents` on the magnitude, sign re-applied: money.ts's largest-remainder hands the spare cent of a negative
 * total to the smallest remainder (see docs/proposals/agent-a.md).
 */
export const allocateSigned = (total, weights) => {
    const sign = total < 0 ? -1 : 1;
    return allocateCents(Math.abs(total), weights).map((c) => (c === 0 ? 0 : sign * c));
};
/** "3 Nov" in the given locale (calendar date, no timezone shift). */
const dateFormats = new Map();
export const shortDate = (d, locale = 'en-AU') => {
    let f = dateFormats.get(locale);
    if (!f) {
        f = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
        dateFormats.set(locale, f);
    }
    const p = parts(d);
    return f.format(new Date(Date.UTC(p.y, p.m - 1, p.d)));
};
/** Append to a Map-of-arrays without copying (O(1) per insert). */
export const pushTo = (m, k, v) => {
    const list = m.get(k);
    if (list)
        list.push(v);
    else
        m.set(k, [v]);
};
/** Throw `KeelError('VALIDATION')` unless `v` is a finite number ≥ `min` (NaN/±Infinity/strings rejected). */
export const requireFinite = (name, v, min = Number.NEGATIVE_INFINITY) => {
    if (typeof v !== 'number' || !Number.isFinite(v) || v < min) {
        throw new KeelError('VALIDATION', `${name} must be a finite number${min > Number.NEGATIVE_INFINITY ? ` ≥ ${min}` : ''}`, { [name]: v });
    }
    return v;
};
export const inRange = (d, from, to) => d >= from && d <= to;
export const isCounted = (t) => t.status !== 'void';
/** Account types whose value is cash-like (spendable / forecastable). */
export const CASH_TYPES = new Set(['transaction', 'savings', 'offset', 'term_deposit', 'cash']);
export const indexBy = (xs) => new Map(xs.map((x) => [x.id, x]));
/** Set containing `rootId` and every descendant category id. */
export const categoryDescendants = (categories, rootId) => {
    const children = new Map();
    for (const c of categories) {
        if (c.parentId === null)
            continue;
        const list = children.get(c.parentId) ?? [];
        list.push(c.id);
        children.set(c.parentId, list);
    }
    const out = new Set([rootId]);
    const stack = [rootId];
    while (stack.length > 0) {
        const id = stack.pop();
        for (const k of children.get(id) ?? [])
            if (!out.has(k)) {
                out.add(k);
                stack.push(k);
            }
    }
    return out;
};
/** Ancestors of a category, nearest first (excludes itself). Cycle-safe. */
export const ancestorsOf = (id, index) => {
    const out = [];
    const seen = new Set([id]);
    let cur = index.get(id)?.parentId ?? null;
    while (cur !== null && !seen.has(cur)) {
        const c = index.get(cur);
        if (!c)
            break;
        out.push(c);
        seen.add(cur);
        cur = c.parentId;
    }
    return out;
};
export const topLevelId = (id, index) => {
    const anc = ancestorsOf(id, index);
    return anc.length > 0 ? anc[anc.length - 1].id : id;
};
/** Nature of a category, inherited from the nearest ancestor when set to 'none'. */
export const effectiveNature = (id, index) => {
    const c = index.get(id);
    if (!c)
        return 'none';
    if (c.nature !== 'none')
        return c.nature;
    for (const a of ancestorsOf(id, index))
        if (a.nature !== 'none')
            return a.nature;
    return c.kind === 'income' ? 'income' : 'none';
};
export const makeSplit = (categoryId, amountCents, extra = {}) => ({
    id: newId(), categoryId, amountCents, memo: '', tagIds: [], gstCents: null, isTaxDeductible: null, ...extra,
});
/** Build a full Transaction with schema defaults (single uncategorised split unless `splits` given). */
export const makeTransaction = (f, now = nowISO()) => ({
    id: newId(), payeeId: null, payeeRaw: '', memo: '', reference: '', status: 'cleared', origin: 'manual',
    splits: [makeSplit(null, f.amountCents)],
    transferId: null, transferAccountId: null, originalAmountCents: null, originalCurrency: null, fxRate: null,
    recurringRuleId: null, importBatchId: null, fingerprint: null, attachmentIds: [], reconciliationId: null,
    isReviewed: true, createdAt: now, updatedAt: now,
    ...f,
});
/** Signed ledger balance per account as at `asOf` (inclusive), opening balance included, void excluded. */
export const balanceAt = (account, txs, asOf) => {
    let b = account.openingBalanceCents;
    for (const t of txs)
        if (t.accountId === account.id && t.date <= asOf && isCounted(t))
            b += t.amountCents;
    return b;
};
//# sourceMappingURL=shared.js.map